import { useState } from "react";
import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { TRANSFER_EDUCATION_PROGRAM_MUTATION } from "../queries";
import Button from "@/components/Button";
import useModal from "@/hooks/useModal";
import { ApolloError } from "@apollo/client";
import Banner from "@/components/Banner";
import { Person } from "@/types";

interface TransferModalProps {
  pgaId: string;
  personData: Person;
  onClosed: () => void;
  refetch?: () => void;
}

export default function TransferModal({ pgaId, personData, onClosed, refetch }: TransferModalProps) {
  const { Modal } = useModal();

  const [transferEducationProgram, { loading: mutationLoading }] = useMutation(TRANSFER_EDUCATION_PROGRAM_MUTATION);
  const [bannerError, setBannerError] = useState("");

  const handleTransfer = async () => {
    try {
      const { data } = await transferEducationProgram({
        variables: {
          input: {
            pgaId
          }
        }
      });

      const { success, message } = data.transferEducationProgram;
      if (success) {
        refetch && refetch();
        showSnackbar("Successfully transferred to automated program", { close: true });
        onClosed();
      } else {
        setBannerError(message);
      }
    } catch (e) {
      setBannerError(`Error: ${(e as ApolloError).message}`);
    }
  };

  const action = (
    <Button disabled={mutationLoading} onClick={handleTransfer}>
      Transfer
    </Button>
  );

  return (
    <Modal
      action={action}
      onClosed={onClosed}
      headline="Transfer to Automated Facility Management Portfolio"
      data-testid="transfer-modal"
      hasFormElements={false}
      className="w-[490px]"
    >
      {!!bannerError && (
        <div className="mb-6">
          <Banner inline variant="error" description="The transfer could not be completed." subText={bannerError} />
        </div>
      )}
      <p>
        Are you sure you wish to transfer {personData.firstName} {personData.lastName} to the Automated Facility
        Management Portfolio? This cannot be undone and will take effect immediately.
      </p>
    </Modal>
  );
}
